/**
 * Live2Kite Foil Assistant: the launcher and a guided conversation (2026-10-05). The faces are AI-generated images of
 * no real person, supplied by Evan: the first ("not a real person, rather a female, blond in her 30s") and eight more on
 * 2026-10-08, assets/l2k-guide-face.jpg and -face-2.jpg to -face-9.jpg, one a day, picked in pickFace().
 *
 * No AI and no server. The flow file (l2k-guide-flow.json, from axis-advisor/scripts/l2k-guide-build.js) holds
 * the questions, the set answers taken from the published policies, and which complete-foil PACKAGES to suggest
 * for each discipline and level. Packages were fit-checked part by part when they were made, so the guide never
 * pairs parts itself; part-by-part building goes to the builder. Prices, the default variant and the delivery
 * line are read live from the store through the Section Rendering API (sections/l2k-guide-product.liquid), so
 * nothing on screen is a stored copy.
 *
 * One front door with Shopify Inbox (option A, 2026-10-12): she is the only chat button; typed questions, a summary
 * of the guide's answers and order tracking go to the Inbox chat on the visitor's tap; Inbox's unread dot shows on her.
 *
 * Honest by construction: the header and first message say it is an automated guide; a person is one tap away;
 * nothing is added to the cart without a tap, and the card says which options it adds.
 *
 * Accessibility: a real <button> launcher, a dialog with a focus trap and Escape, a polite live log, 44px
 * targets, no sound, and no animation under prefers-reduced-motion.
 */
(function () {
  'use strict';
  var root = document.getElementById('L2KGuide');
  if (!root || root.getAttribute('data-ready')) return;
  root.setAttribute('data-ready', '1');

  var FLOW_URL = root.getAttribute('data-flow');
  var KEY = 'l2k-guide-v1';
  var PHOTO = pickFace();

  // A different face each day (Evan, 2026-10-08: "Can we have a different avatar each day?", then "Have the avatars on
  // a randomized"): nine AI-generated characters of no real person, data-photos in the snippet. The day's face is a
  // pseudo-random pick seeded by the Pacific date, so everyone sees the same face that day and it changes at midnight
  // shop time, never twice in a row; a visitor keeps the face they first saw for the whole visit. ?l2kface=N (1 to 9)
  // shows face N, for checking a preview.
  function pickFace() {
    var list = (root.getAttribute('data-photos') || '').split(',').filter(Boolean);
    var fallback = root.getAttribute('data-photo');
    if (!list.length) return fallback;
    var forced = parseInt((location.search.match(/[?&]l2kface=(\d+)/) || [])[1], 10);
    if (forced >= 1 && forced <= list.length) return list[forced - 1];
    try { var kept = sessionStorage.getItem(KEY + '-face'); if (kept && list.indexOf(kept) >= 0) return kept; } catch (e) {}
    var n = list.length, day = pacificDay();
    var i = n > 1 ? shuffled(Math.floor(day / n), n)[day % n] : 0;
    try { sessionStorage.setItem(KEY + '-face', list[i]); } catch (e) {}
    return list[i];
  }
  // Each block of n days shows every face once, in an order shuffled by the block number (mulberry32 seed, then
  // Fisher-Yates); if a block would open with the face the previous one closed on, its first two swap.
  function shuffled(block, n) {
    var order = shuffleOnly(block, n), prev = shuffleOnly(block - 1, n);
    if (order[0] === prev[n - 1]) { var t = order[0]; order[0] = order[1]; order[1] = t; }
    return order;
  }
  function shuffleOnly(block, n) {
    var a = [], s = (block * 2654435761) >>> 0;
    for (var k = 0; k < n; k++) a.push(k);
    for (var m = n - 1; m > 0; m--) {
      s = (s + 0x6D2B79F5) >>> 0; var x = Math.imul(s ^ (s >>> 15), 1 | s);
      x = (x + Math.imul(x ^ (x >>> 7), 61 | x)) ^ x; x = ((x ^ (x >>> 14)) >>> 0) / 4294967296;
      var r = Math.floor(x * (m + 1)), t = a[m]; a[m] = a[r]; a[r] = t;
    }
    return a;
  }
  function pacificDay() {
    try {
      var p = {};
      new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', year: 'numeric', month: 'numeric', day: 'numeric' })
        .formatToParts(new Date()).forEach(function (x) { p[x.type] = x.value; });
      return Math.floor(Date.UTC(+p.year, +p.month - 1, +p.day) / 864e5);
    } catch (e) { return Math.floor(Date.now() / 864e5); }
  }
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var flow = null;
  var state = load() || { open: false, path: [] };
  var launcher, panel, log, choices, ask, askInput, lastFocus;

  function load() { try { return JSON.parse(sessionStorage.getItem(KEY)); } catch (e) { return null; } }
  function save() { try { sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (e) { /* private mode: fine */ } }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function money(cents) {
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100); }
    catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  function find(list, key) { for (var i = 0; i < list.length; i++) if (list[i].key === key) return list[i]; return null; }

  // ---------- the shell ----------
  function build() {
    launcher = el('button', 'l2kg-launcher');
    launcher.type = 'button';
    launcher.setAttribute('aria-haspopup', 'dialog');
    launcher.setAttribute('aria-label', 'Chat with the Live2Kite Foil Assistant');
    var img = el('img');
    img.src = PHOTO; img.alt = ''; img.width = 56; img.height = 56; img.decoding = 'async';
    launcher.appendChild(img);
    launcher.appendChild(el('span', 'l2kg-label', 'Chat'));
    var tip = el('span', 'l2kg-tip', 'Need a hand choosing a foil?');
    tip.setAttribute('aria-hidden', 'true');
    launcher.appendChild(tip);
    // While the shop has replied in the chat and the reply is unread, a tap on her opens the chat itself.
    launcher.addEventListener('click', function () { if (launcher.classList.contains('l2kg-unread') && openChat()) return; open(); });

    panel = el('div', 'l2kg-panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-labelledby', 'l2kg-title');
    panel.hidden = true;

    var head = el('div', 'l2kg-head');
    var face = el('img', 'l2kg-face');
    face.src = PHOTO; face.alt = 'Live2Kite Foil Assistant, an AI-generated character'; face.width = 44; face.height = 44;
    var titles = el('div', 'l2kg-titles');
    var h = el('h2', 'l2kg-title', 'Live2Kite Foil Assistant');
    h.id = 'l2kg-title';
    titles.appendChild(h);
    titles.appendChild(el('p', 'l2kg-sub', 'Automated guide, Live2Kite Sausalito'));
    var close = el('button', 'l2kg-close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close the guide');
    close.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    close.addEventListener('click', shut);
    head.appendChild(face); head.appendChild(titles); head.appendChild(close);

    log = el('div', 'l2kg-log');
    log.setAttribute('role', 'log');
    log.setAttribute('aria-live', 'polite');
    log.setAttribute('aria-relevant', 'additions');
    log.tabIndex = 0;
    log.setAttribute('aria-label', 'Conversation');

    choices = el('div', 'l2kg-choices');
    choices.setAttribute('role', 'group');
    choices.setAttribute('aria-label', 'Your answer');

    // "Ask us anything": the visitor's own words go to the Inbox chat (option A, Evan 2026-10-12). Hidden until the
    // Inbox embed is on the page, so the guide still works on its own if Inbox is ever switched off.
    ask = el('form', 'l2kg-ask');
    ask.hidden = true;
    ask.setAttribute('aria-label', 'Ask the shop a question');
    askInput = el('input');
    askInput.type = 'text'; askInput.name = 'q'; askInput.maxLength = 500; askInput.autocomplete = 'off';
    askInput.placeholder = 'Ask us anything...';
    askInput.setAttribute('aria-label', 'Ask us anything. Your question opens the shop chat.');
    var send = el('button', 'l2kg-send');
    send.type = 'submit';
    send.setAttribute('aria-label', 'Send to the shop chat');
    send.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M4 12l16-8-6 16-2.5-6.5L4 12z" fill="currentColor"/></svg>';
    ask.appendChild(askInput); ask.appendChild(send);
    ask.addEventListener('submit', function (e) {
      e.preventDefault();
      var q = askInput.value.trim();
      if (!q) { askInput.focus(); return; }
      // Called inside the submit event: Shopify accepts a starter message only during a real tap or key press.
      if (openChat(q.slice(0, 500), 'typed')) askInput.value = '';
    });

    panel.appendChild(head); panel.appendChild(log); panel.appendChild(choices); panel.appendChild(ask);
    panel.addEventListener('keydown', onKey);
    // Following a link from the guide (a product, the builder, a policy) leaves the conversation saved but the
    // panel closed, so the next page is visible; on a phone the panel would otherwise cover it.
    panel.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href]');
      if (a && !/^(tel|mailto|sms):/.test(a.getAttribute('href'))) { state.open = false; save(); }
    });
    root.appendChild(launcher);
    root.appendChild(panel);
    stack();
    setTimeout(stack, 2500); setTimeout(stack, 8000);
    // The Inbox embed loads on its own schedule: look for it for up to 30 s.
    var tries = 0;
    (function poll() { if (!watchInbox() && ++tries < 30) setTimeout(poll, 1000); })();
    window.addEventListener('resize', stack);

    // A first-visit hint on wide screens only; a phone gets just the face.
    if (!state.hinted && window.innerWidth >= 750) {
      setTimeout(function () { if (!state.open) launcher.classList.add('l2kg-hint'); }, 4000);
      setTimeout(function () { launcher.classList.remove('l2kg-hint'); }, 14000);
      state.hinted = true; save();
    }
  }

  // Another app's launcher in the bottom-right corner (Smile.io Rewards on Live2Kite): sit just above it.
  function stack() {
    var other = document.getElementById('smile-ui-lite-launcher-frame-container');
    var r = other && other.getBoundingClientRect();
    var html = document.documentElement;
    if (r && r.width && r.height && r.right > window.innerWidth - 120) {
      html.style.setProperty('--l2kg-stack', Math.round(window.innerHeight - r.top + 12) + 'px');
      html.classList.add('l2kg-stack');
    } else html.classList.remove('l2kg-stack');
  }

  // ---------- Shopify Inbox: one front door (option A, Evan 2026-10-12: "Let's Preview option A") ----------
  // The Inbox app embed puts <shopify-chat> on the page. Its own button is hidden where the guide runs
  // (l2k-guide.css, ::part(activator)), so she is the only chat button. show() opens Inbox's chat, where its AI agent
  // answers at once and a person replies in shop hours; sendStarterMessage(text, kind) posts a message as the visitor
  // and Shopify accepts it only during a real tap or key press, so it is only ever called from one. Inbox's unread
  // dot lives in its button's open shadow root (.activator__indicator--active, read 2026-10-12): it is mirrored onto
  // her launcher. The chat itself runs in Shopify's own frame; nothing here can read or change it.
  function inbox() { var c = document.querySelector('shopify-chat'); return c && typeof c.show === 'function' ? c : null; }
  function openChat(message, kind) {
    var c = inbox();
    if (!c) return false;
    if (!panel.hidden) shut();
    c.show();
    if (message && typeof c.sendStarterMessage === 'function') {
      try { var p = c.sendStarterMessage(message, kind || 'typed'); if (p && p.catch) p.catch(function () {}); } catch (e) { /* the chat is open; they can type */ }
    }
    return true;
  }
  // Inbox rebuilds its button inside a nested shadow root (<shopify-agent-runtime>) once the chat has loaded, so
  // the dot is searched for, and watched, through every open shadow root under <shopify-chat> (found testing 2026-10-12).
  function deepFind(root, sel, depth) {
    if (!root || depth > 4) return null;
    var hit = root.querySelector(sel);
    if (hit) return hit;
    var all = root.querySelectorAll('*');
    for (var i = 0; i < all.length; i++) if (all[i].shadowRoot) { hit = deepFind(all[i].shadowRoot, sel, depth + 1); if (hit) return hit; }
    return null;
  }
  function watchInbox() {
    if (watchInbox.done) return true;
    var c = inbox();
    if (!c) return false;
    watchInbox.done = true;
    ask.hidden = false;
    var seen = [], queued = false;
    function sync() {
      queued = false;
      watch(c.shadowRoot, 0);
      var on = !!deepFind(c.shadowRoot, '.activator__indicator--active', 0);
      launcher.classList.toggle('l2kg-unread', on);
      launcher.setAttribute('aria-label', on ? 'Chat: you have a reply from the shop' : 'Chat with the Live2Kite Foil Assistant');
    }
    var mo = window.MutationObserver ? new MutationObserver(function () { if (!queued) { queued = true; requestAnimationFrame(sync); } }) : null;
    function watch(root, depth) {
      if (!mo || !root || depth > 4) return;
      if (seen.indexOf(root) < 0) { seen.push(root); mo.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] }); }
      var all = root.querySelectorAll('*');
      for (var i = 0; i < all.length; i++) if (all[i].shadowRoot) watch(all[i].shadowRoot, depth + 1);
    }
    sync();
    return true;
  }

  function onKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); shut(); return; }
    if (e.key !== 'Tab') return;
    var f = panel.querySelectorAll('button:not([disabled]), a[href], input:not([hidden]), [tabindex="0"]');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function open() {
    lastFocus = document.activeElement;
    state.open = true; save();
    launcher.classList.remove('l2kg-hint');
    panel.hidden = false;
    document.documentElement.classList.add('l2kg-is-open');
    requestAnimationFrame(function () { panel.classList.add('l2kg-panel--in'); });
    if (!log.childNodes.length) replay();
    focusChoice();
  }
  function shut() {
    state.open = false; save();
    panel.classList.remove('l2kg-panel--in');
    document.documentElement.classList.remove('l2kg-is-open');
    panel.hidden = true;
    setTimeout(stack, 50);
    (lastFocus && lastFocus !== document.body && lastFocus.focus && document.contains(lastFocus) ? lastFocus : launcher).focus();
  }
  function focusChoice() {
    var b = choices.querySelector('button, a');
    (b || log).focus({ preventScroll: true });
  }

  // ---------- conversation ----------
  function say(text, opts) {
    var b = el('div', 'l2kg-msg l2kg-msg--bot');
    if (typeof text === 'string') b.textContent = text; else b.appendChild(text);
    if (opts && opts.link) {
      var a = el('a', 'l2kg-link', opts.link.text);
      a.href = opts.link.href;
      if (/^https?:/.test(opts.link.href)) { a.target = '_blank'; a.rel = 'noopener'; }
      b.appendChild(document.createTextNode(' '));
      b.appendChild(a);
    }
    log.appendChild(b);
    scrollDown();
    return b;
  }
  function echo(text) {
    var b = el('div', 'l2kg-msg l2kg-msg--me', text);
    log.appendChild(b);
    scrollDown();
  }
  function scrollDown() { log.scrollTop = log.scrollHeight; }

  // Offer answers. Each choice is [label, step, args]; an href choice is a link instead of a step.
  function offer(list) {
    choices.innerHTML = '';
    list.filter(Boolean).forEach(function (c) {
      var n;
      if (c.href) {
        n = el('a', 'l2kg-chip' + (c.primary ? ' l2kg-chip--primary' : ''), c.label);
        n.href = c.href;
      } else if (c.action) {
        // An action (open the live chat) is not a step: it must not replay on the next page.
        n = el('button', 'l2kg-chip' + (c.primary ? ' l2kg-chip--primary' : ''), c.label);
        n.type = 'button';
        n.addEventListener('click', c.action);
      } else {
        n = el('button', 'l2kg-chip' + (c.primary ? ' l2kg-chip--primary' : ''), c.label);
        n.type = 'button';
        n.addEventListener('click', function () { go(c.step, c.args || [], c.label); });
      }
      choices.appendChild(n);
    });
  }

  function go(step, args, label) {
    if (label) echo(label);
    state.path.push([step, args, label || null]);
    save();
    run(step, args, false);
    if (!panel.hidden) focusChoice();
  }

  // Rebuild the conversation after a page change, without re-fetching anything twice.
  function replay() {
    log.innerHTML = '';
    if (!state.path.length) state.path = [['start', [], null]];
    state.path.forEach(function (p, i) {
      if (p[2]) echo(p[2]);
      run(p[0], p[1], i < state.path.length - 1);
    });
    save();
  }

  var MENU = [
    { label: 'Find me a complete foil', step: 'discipline', primary: true },
    { label: 'Will it fit my foil?', step: 'answer', args: ['fit'] },
    { label: 'Shipping and delivery', step: 'answer', args: ['shipping'] },
    { label: 'Returns', step: 'answer', args: ['returns'] },
    { label: 'My order', step: 'answer', args: ['order'] },
    { label: 'Warranty', step: 'answer', args: ['warranty'] },
    { label: 'Foil Drive help', step: 'answer', args: ['foildrive'] },
    { label: 'The shop and pickup', step: 'answer', args: ['pickup'] },
    { label: 'Talk to a person', step: 'person' },
  ];

  var STEPS = {
    start: function () {
      say(inbox()
        ? "Hi, I'm the Live2Kite Foil Assistant, an automated guide. I can find you a complete AXIS foil in three taps or answer the usual questions. Or type anything below: it goes to our chat, where an AI assistant answers right away and a person replies " + hoursText() + "."
        : "Hi, I'm the Live2Kite Foil Assistant, an automated guide. I can find you a complete AXIS foil in three taps, or answer the usual questions. For a real person, tap Talk to a person.");
      offer(MENU);
    },
    menu: function () {
      say('What else can I help with?');
      offer(MENU);
    },
    discipline: function () {
      say('What do you ride, or want to ride?');
      offer(flow.disciplines.map(function (d) { return { label: d.label, step: 'level', args: [d.key] }; }));
    },
    level: function (d) {
      say('And where are you at?');
      offer(flow.levels.map(function (l) { return { label: l.label, step: 'weight', args: [d, l.key] }; })
        .concat([{ label: 'Back', step: 'discipline' }]));
    },
    weight: function (d, l) {
      say('Roughly what do you weigh? It helps pick the size.');
      offer(flow.weights.map(function (w) { return { label: w.label, step: 'results', args: [d, l, w.key] }; })
        .concat([{ label: 'Skip', step: 'results', args: [d, l, null] }]));
    },
    results: function (d, l, w, quiet) {
      var disc = find(flow.disciplines, d), lev = find(flow.levels, l), wt = w ? find(flow.weights, w) : null;
      var picks = ((flow.routes[d] || {})[l] || []).slice();
      var notes = [];
      if (wt) {
        picks = picks.filter(function (p) {
          if (p.maxKg && wt.kg >= p.maxKg) { if (p.overNote) notes.push(p.overNote); return false; }
          return true;
        });
        // heavier riders see the bigger wing first, lighter riders the smaller
        if (w === 'heavy' || w === 'xheavy') picks.sort(function (a, b) { return b.size - a.size; });
        if (w === 'light') picks.sort(function (a, b) { return a.size - b.size; });
        // a pick marked lead stays first whatever the weight (an AXIS call, not a size rule)
        picks = picks.filter(function (p) { return p.lead; }).concat(picks.filter(function (p) { return !p.lead; }));
      }
      say('For ' + disc.label.toLowerCase() + ', ' + lev.label.toLowerCase() + ', here is where I would start. Each is a complete foil: front wing, rear wing, fuselage and mast, ready to bolt together.');
      var wrap = el('div', 'l2kg-cards');
      log.appendChild(wrap);
      picks.forEach(function (p) { wrap.appendChild(card(p)); });
      notes.forEach(function (n) { say(n); });
      scrollDown();
      offer([
        // Sends a summary in the visitor's name, so the label says exactly that and it goes only on their tap.
        inbox() ? { label: 'Send my answers to the shop chat', primary: true, action: function () {
          var titles = [].map.call(wrap.querySelectorAll('.l2kg-card-title'), function (t) { return t.textContent; });
          var msg = 'Hi, I am looking for a complete foil: ' + disc.label.toLowerCase() + ', ' + lev.label.toLowerCase() + (wt ? ', ' + wt.label : '') + '.' +
            (titles.length ? ' The Foil Assistant suggested: ' + titles.join('; ') + '.' : '') + ' Can you help me choose?';
          openChat(msg.slice(0, 500), 'typed');
        } } : null,
        { label: 'Build it part by part', href: '/pages/build-a-foil' },
        { label: 'Start again', step: 'discipline' },
        { label: 'Talk to a person', step: 'person' },
        { label: 'Other questions', step: 'menu' },
      ]);
    },
    answer: function (key) {
      var a = flow.answers[key];
      say(a.text, { link: a.link });
      offer([
        // Inbox has Shopify's own order lookup; this is the same "Track my order" its chat offers as a button.
        key === 'order' && inbox() ? { label: 'Track my order in the chat', primary: true, action: function () { openChat('Track my order', 'suggestion_pill'); } } : null,
        { label: 'Find me a complete foil', step: 'discipline', primary: key !== 'order' || !inbox() },
        { label: 'Other questions', step: 'menu' },
        { label: 'Talk to a person', step: 'person' },
      ]);
    },
    person: function () {
      var c = flow.contact;
      var openNow = shopOpen(c.hours);
      var textLine = c.sms ? ', text ' + c.text : '';
      var hoursLabel = (c.hours && c.hours.label) || 'Monday to Friday, 10am to 5:30pm Pacific';
      // Live chat is Shopify Inbox (Evan, 2026-10-08: "Like a live chat"): its app embed puts <shopify-chat> on the
      // page, and show() opens the chat panel. The chat opens empty; nothing is sent in the customer's name.
      var chat = document.querySelector('shopify-chat');
      var live = !!(chat && typeof chat.show === 'function');
      if (live) {
        say(openNow
          ? 'Someone in the shop is available now. Chat with us here, or call ' + c.phone + textLine + '.'
          : 'No one is available right now; we answer ' + hoursLabel + '. Leave a message in the chat with your email and we will reply then, or text ' + (c.sms ? c.text : c.phone) + '.');
      } else
      say(openNow
        ? 'Someone in the shop is available now. Call us on ' + c.phone + textLine + ', or email ' + c.email + '.'
        : 'No one is available right now; we answer ' + hoursLabel + '. Text ' + (c.sms ? c.text + ' or email ' : '') + c.email + ' and we will reply then, or call ' + c.phone + ' in those hours.');
      var subject = 'Question from live2kite.com';
      var body = context();
      offer([
        live ? { label: 'Chat with us now', primary: true, action: function () { openChat(); } } : null,
        { label: 'Call ' + c.phone, href: 'tel:' + c.tel, primary: openNow && !live },
        // 415.924.6062 is the shop number that takes texts. "?&body=" opens prefilled on both iPhone and Android.
        c.sms ? { label: 'Text ' + c.text, href: 'sms:' + c.sms + '?&body=' + encodeURIComponent('Hi Live2Kite, ' + (body || '')) } : null,
        { label: 'Email the shop', href: 'mailto:' + c.email + '?subject=' + encodeURIComponent(subject) + (body ? '&body=' + encodeURIComponent(body) : ''), primary: !openNow && !live },
        { label: 'Other questions', step: 'menu' },
      ]);
    },
  };

  function run(step, args, quiet) {
    var f = STEPS[step] || STEPS.start;
    f.apply(null, (args || []).concat([quiet]));
  }

  // What the customer has told the guide, so an email to the shop does not start from nothing.
  function context() {
    for (var i = state.path.length - 1; i >= 0; i--) {
      if (state.path[i][0] === 'results') {
        var a = state.path[i][1];
        var d = find(flow.disciplines, a[0]), l = find(flow.levels, a[1]), w = a[2] ? find(flow.weights, a[2]) : null;
        return '\n\nFrom the website guide: ' + d.label + ', ' + l.label.toLowerCase() + (w ? ', ' + w.label : '') + '.';
      }
    }
    return '';
  }

  function hoursText() {
    var h = flow && flow.contact && flow.contact.hours;
    return 'Monday to Friday, ' + ((h && h.label) ? h.label.replace(/^Monday to Friday,?\s*/i, '').replace(/\s*Pacific$/i, '') : '10am to 5:30pm') + ' Pacific';
  }

  function shopOpen(h) {
    try {
      var parts = new Intl.DateTimeFormat('en-US', { timeZone: h.tz, weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23' }).formatToParts(new Date());
      var wd = '', hr = 0;
      parts.forEach(function (p) {
        if (p.type === 'weekday') wd = p.value;
        if (p.type === 'hour') hr += parseInt(p.value, 10);
        if (p.type === 'minute') hr += parseInt(p.value, 10) / 60;   // 17.5 = 5:30pm
      });
      var day = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(wd);
      return h.days.indexOf(day) >= 0 && hr >= h.open && hr < h.close;
    } catch (e) { return false; }
  }

  // ---------- product cards, live from the store ----------
  var cache = {};
  function product(handle) {
    if (!cache[handle]) {
      cache[handle] = fetch('/products/' + encodeURIComponent(handle) + '?sections=l2k-guide-product', { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(function (j) {
          var html = j['l2k-guide-product'];
          var tmp = document.createElement('div');
          tmp.innerHTML = html || '';
          var s = tmp.querySelector('[data-l2k-guide-product]');
          if (!s) throw new Error('no product data');
          return JSON.parse(s.textContent);
        });
    }
    return cache[handle];
  }

  function card(pick) {
    var c = el('article', 'l2kg-card l2kg-card--loading');
    c.appendChild(el('p', 'l2kg-why', pick.why));
    product(pick.handle).then(function (p) {
      c.classList.remove('l2kg-card--loading');
      if (!p.available) { c.remove(); return; }
      var a = el('a', 'l2kg-card-link');
      a.href = p.url;
      if (p.image) {
        var im = el('img', 'l2kg-card-img');
        im.src = p.image; im.alt = p.imageAlt || ''; im.loading = 'lazy'; im.width = 120; im.height = 120;
        a.appendChild(im);
      }
      var t = el('div', 'l2kg-card-body');
      t.appendChild(el('h3', 'l2kg-card-title', p.title));
      var price = el('p', 'l2kg-price', (p.priceMin !== p.priceMax ? 'From ' : '') + money(p.priceMin));
      t.appendChild(price);
      a.appendChild(t);
      c.insertBefore(a, c.firstChild);
      if (p.variant && p.variant.delivery && p.variant.delivery.t) c.appendChild(el('p', 'l2kg-delivery', p.variant.delivery.t));
      var acts = el('div', 'l2kg-acts');
      if (p.variant && p.variant.available) {
        var add = el('button', 'l2kg-btn l2kg-btn--primary', p.variant.delivery && p.variant.delivery.b ? p.variant.delivery.b : 'Add to cart');
        add.type = 'button';
        var as = p.variantCount > 1 && p.variant.title !== 'Default Title' ? p.variant.title.split(' / ').join(', ') : '';
        if (as) add.setAttribute('aria-label', (add.textContent) + ': ' + p.title + ', ' + as);
        add.addEventListener('click', function () { addToCart(p, add, c); });
        acts.appendChild(add);
        if (as) c.appendChild(el('p', 'l2kg-as', 'Adds it as: ' + as + '. Other options on the product page.'));
      }
      var more = el('a', 'l2kg-btn', p.variantCount > 1 ? 'See options' : 'View');
      more.href = p.url;
      acts.appendChild(more);
      c.appendChild(acts);
    }).catch(function () {
      c.classList.remove('l2kg-card--loading');
      var a = el('a', 'l2kg-link', 'See this package');
      a.href = '/products/' + pick.handle;
      c.appendChild(a);
    });
    return c;
  }

  function addToCart(p, btn, c) {
    btn.disabled = true;
    var label = btn.textContent;
    btn.textContent = 'Adding...';
    var item = { id: p.variant.id, quantity: 1 };
    if (p.variant.delivery && p.variant.delivery.p) item.properties = { 'Pre-order': p.variant.delivery.p };
    fetch('/cart/add.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item], sections: 'cart-icon-bubble', sections_url: window.location.pathname })
    }).then(function (r) { return r.json().then(function (j) { if (!r.ok) throw new Error(j.description || j.message || r.status); return j; }); })
      .then(function (j) {
        btn.textContent = 'Added';
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble && j.sections && j.sections['cart-icon-bubble']) {
          var tmp = document.createElement('div');
          tmp.innerHTML = j.sections['cart-icon-bubble'];
          var fresh = tmp.querySelector('.shopify-section') || tmp;
          bubble.innerHTML = fresh.innerHTML;
        }
        var ok = el('p', 'l2kg-added');
        ok.appendChild(document.createTextNode('In your cart. '));
        var a = el('a', 'l2kg-link', 'View cart');
        a.href = '/cart';
        ok.appendChild(a);
        c.appendChild(ok);
        say('Added to your cart: ' + p.title + '.');
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = label;
        c.appendChild(el('p', 'l2kg-warn', 'The cart would not take that just now. Open the product page, or call us on ' + flow.contact.phone + '.'));
      });
  }

  // ---------- boot ----------
  function boot() {
    fetch(FLOW_URL).then(function (r) { return r.json(); }).then(function (f) {
      flow = f;
      build();
      if (state.open) open();
    }).catch(function () { /* no guide is better than a broken one */ });
  }
  var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 1200); };
  if (document.readyState === 'complete') idle(boot); else window.addEventListener('load', function () { idle(boot); });
})();
